import React from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, Sprout, Target, Trophy } from 'lucide-react';
import {
  Heading,
  Text,
  Stack,
} from '@/components/ui/index.js';
import styles from './SkillLevelStep.module.css';

/**
 * Skill level options with icons and descriptions
 */
const SKILL_LEVELS = [
  {
    id: 'beginner',
    label: 'Beginner',
    description: 'Just starting out or play casually',
    icon: Sprout,
  },
  {
    id: 'intermediate',
    label: 'Intermediate',
    description: 'Regular player with some experience',
    icon: Target,
  },
  {
    id: 'advanced',
    label: 'Advanced',
    description: 'Competitive player or athlete',
    icon: Trophy,
  },
];

/**
 * SkillLevelStep - Skill level selection
 * 
 * Features:
 * - 3 skill level options
 * - Single select
 * - Icon and description for each level
 * - Continue button
 */
export function SkillLevelStep({
  selectedLevel,
  onLevelSelect,
  onContinue,
  onBack,
}) {
  const hasSelection = selectedLevel !== null;

  return (
    <Stack direction="column" gap={6} fullHeight>
      {/* Header */}
      <Stack direction="column" gap={3}>
        <button
          type="button"
          onClick={onBack}
          className={styles.backButton}
          aria-label="Go back"
        >
          <ChevronLeft size={20} />
          <span>Back</span>
        </button>

        <Stack direction="column" gap={2}>
          <Heading level={2} size="lg" color="primary" align="left">
            What&apos;s your skill level?
          </Heading>
          <Text size="base" color="secondary" align="left">
            This helps us match you with the right activities.
          </Text>
        </Stack>
      </Stack>

      {/* Skill Level Options */}
      <Stack direction="column" gap={3}>
        {SKILL_LEVELS.map((level) => {
          const IconComponent = level.icon;
          const isSelected = selectedLevel === level.id;

          return (
            <button
              key={level.id}
              type="button"
              onClick={() => onLevelSelect(level.id)}
              className={`${styles.levelCard} ${isSelected ? styles.levelCardSelected : ''}`}
              aria-pressed={isSelected}
            >
              <div className={styles.levelCardGlow} />
              
              <div className={styles.levelContent}>
                <div className={`${styles.levelIcon} ${isSelected ? styles.levelIconSelected : ''}`}>
                  <IconComponent size={24} />
                </div>
                
                <Stack direction="column" gap={1} className={styles.levelText}>
                  <Text 
                    size="base" 
                    weight="semibold" 
                    color={isSelected ? 'cyan' : 'primary'}
                  >
                    {level.label}
                  </Text>
                  <Text size="sm" color="secondary">
                    {level.description}
                  </Text>
                </Stack>
              </div>

              {/* Selection indicator */}
              <div className={`${styles.radioIndicator} ${isSelected ? styles.radioIndicatorSelected : ''}`}>
                <div className={styles.radioDot} />
              </div>
            </button>
          );
        })}
      </Stack>

      {/* Continue Button */}
      <div className={styles.footer}>
        <button
          type="button"
          onClick={onContinue}
          disabled={!hasSelection}
          className={`${styles.continueButton} ${hasSelection ? styles.continueButtonActive : ''}`}
        >
          Continue
        </button>
      </div>
    </Stack>
  );
}

SkillLevelStep.propTypes = {
  selectedLevel: PropTypes.string,
  onLevelSelect: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

SkillLevelStep.defaultProps = {
  selectedLevel: null,
};

export default SkillLevelStep;
